import { useState } from "react";

import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { Alert, Button, Snackbar, Stack, Typography } from "@mui/material";

import { CompanyLogoTile } from "components/CompanyLogoTile";
import { SUBSCRIPTION_PLAN_COLORS } from "constants/index";
import { useCarrierStats, useDisclosure, useRoleContext } from "hooks";
import { capitalizeFirst, ISOStringToMonthDayYear } from "utils";

import { EditCompanyLogoModal } from "../EditCompanyLogoModal";

import type { CompanyProfile } from "types/company";

import type { LogoActionKind } from "../EditCompanyLogoModal";

const LOGO_TILE_HEADER_SIZE_PX = 72;
const TOAST_AUTO_HIDE_MS = 3000;

const LOGO_ACTION_MESSAGES: Record<LogoActionKind, string> = {
  updated: "Logo updated successfully.",
  removed: "Logo removed.",
};

interface Props {
  companyProfile: CompanyProfile;
  onEditProfile: VoidFunction;
  onLogoChanged: VoidFunction;
}

export function CompanyProfileHeader(props: Props) {
  const { companyProfile, onEditProfile, onLogoChanged } = props;
  const { company, hasCompanyAdminRole } = useRoleContext();
  const { carrierMeta } = useCarrierStats({
    companyId: companyProfile.company.id,
  });
  const isOwnCompany = company?.id === companyProfile.company.id;
  const isCarrier = company?.company_type === "CARRIER";
  const shouldShowEditButton =
    isOwnCompany && isCarrier && hasCompanyAdminRole();

  const logoModal = useDisclosure();
  const toast = useDisclosure();
  const [toastMessage, setToastMessage] = useState("");

  // The Snackbar lives here rather than in the modal: the modal unmounts on
  // close, so a modal-owned toast would be destroyed the instant it should
  // appear.
  const handleLogoActionSuccess = (kind: LogoActionKind) => {
    setToastMessage(LOGO_ACTION_MESSAGES[kind]);
    toast.onOpen();
    onLogoChanged();
  };

  return (
    <Stack direction="column">
      <Stack spacing={1} sx={{ mb: "2rem" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <CompanyLogoTile
              companyName={companyProfile.company.name}
              logoDownloadUrl={companyProfile.logo?.download_url ?? null}
              size={LOGO_TILE_HEADER_SIZE_PX}
              editable={shouldShowEditButton}
              onEditClick={logoModal.onOpen}
            />
            <Typography variant="h3">{companyProfile?.company.name}</Typography>
          </Stack>
          {shouldShowEditButton && (
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              sx={{
                position: "sticky",
                top: 16,
                zIndex: 10,
              }}
              onClick={onEditProfile}
            >
              Edit Profile
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Stack direction="row" spacing={0.5}>
            <CalendarTodayOutlinedIcon color="primary" />
            <Typography variant="body1">
              Member since{" "}
              {companyProfile?.company?.created_at &&
                ISOStringToMonthDayYear(companyProfile.company.created_at)}
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary">
            •
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <MapOutlinedIcon color="primary" />
            <Typography variant="body1">
              Located In:{" "}
              {`${companyProfile?.address.city}, ${companyProfile?.address.country ?? ""}`}
            </Typography>
          </Stack>
          {carrierMeta && (
            <>
              <Typography variant="body1" color="text.secondary">
                •
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <WorkspacePremiumIcon
                  sx={{
                    color:
                      SUBSCRIPTION_PLAN_COLORS[
                        carrierMeta.subscriptionPlan.toLowerCase()
                      ],
                  }}
                />
                <Typography variant="body1">
                  Account Tier: {capitalizeFirst(carrierMeta.subscriptionPlan)}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>

      {shouldShowEditButton && (
        <EditCompanyLogoModal
          companyProfile={companyProfile}
          modalOpen={logoModal.isOpen}
          onModalClose={logoModal.onClose}
          onLogoActionSuccess={handleLogoActionSuccess}
        />
      )}

      <Snackbar
        open={toast.isOpen}
        onClose={toast.onClose}
        autoHideDuration={TOAST_AUTO_HIDE_MS}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={toast.onClose}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
